import type { Account } from '../types/account'
import { defaultVpcAvailabilityZonesForAccountType } from './accountRequestNetworkSummary'

const NON_PROD_TYPE = 'Non-Production'

export function isStgOnboardingSourceAccountType(accountType: string | undefined): boolean {
	return String(accountType ?? '').trim() === NON_PROD_TYPE
}

const CLEARED_NETWORK_FIELDS: Record<string, string> = {
	vpcName: '',
	vpcCidrBlock: '',
	privateSubnetsCidr: '',
	tgwSubnetsCidr: '',
	requestTgwAttachment: 'true',
	isCoreVpc: 'false',
	createPrivateHostedZone: 'true',
	associateDnsResolverRules: 'true',
	useCentralizedVpcInterfaceEndpoints: 'true',
	privateHostedZoneName: ''
}

type SsoUser = { userName: string; displayName: string }

function normalizeSsoList(raw: unknown): SsoUser[] {
	if (Array.isArray(raw)) {
		return raw.map((item) => {
			if (item && typeof item === 'object' && 'userName' in item) {
				return {
					userName: String((item as SsoUser).userName ?? ''),
					displayName: String((item as SsoUser).displayName ?? '')
				}
			}
			return { userName: '', displayName: '' }
		})
	}
	if (typeof raw === 'string' && raw.trim()) {
		return raw.split(',').map((s) => ({
			userName: s.trim(),
			displayName: ''
		}))
	}
	return []
}

function getPerTypeDetails(
	details: Record<string, unknown>,
	accountType: string
): Record<string, unknown> {
	const nested = details[accountType]
	if (
		nested &&
		typeof nested === 'object' &&
		!Array.isArray(nested) &&
		('distributionList' in (nested as object) || 'platformOwner' in (nested as object))
	) {
		return nested as Record<string, unknown>
	}
	return details
}

export function buildStgOnboardingAccountFormValues(
	account: Account
): Record<string, unknown> | null {
	if (!isStgOnboardingSourceAccountType(account.type)) return null
	const d = (account.details ?? {}) as Record<string, unknown>
	const typeValue = String(account.type).trim()
	const block = getPerTypeDetails(d, typeValue)
	const p = (s: string) => String((block as Record<string, unknown>)[s] ?? d[s] ?? '')
	const capRaw = block.monthlyBudgetCap ?? d.monthlyBudgetCap
	const monthlyBudgetCap =
		capRaw === undefined || capRaw === null || capRaw === ''
			? 1
			: typeof capRaw === 'number'
				? capRaw
				: Number(capRaw)

	return {
		accountTypes: [{ label: typeValue, value: typeValue }],
		accountWorkloadType: (d.accountWorkloadType as 'EKS' | 'Non-EKS') || 'Non-EKS',
		[`${typeValue}DistributionList`]: p('distributionList'),
		[`${typeValue}MonthlyBudgetCap`]: Number.isFinite(monthlyBudgetCap) ? monthlyBudgetCap : 1,
		[`${typeValue}platformOwner`]: p('platformOwner'),
		[`${typeValue}SSOUserNames`]: normalizeSsoList(block.SSOUserNames ?? d.SSOUserNames),
		...CLEARED_NETWORK_FIELDS,
		vpcAvailabilityZones: defaultVpcAvailabilityZonesForAccountType(account.type),
		addSecondaryCidrBlock: false,
		secondaryCidrBlock: '',
		addPublicCidrBlock: false,
		publicCidrBlock: '',
		ip_address_allocations: [] as Array<{
			id: string
			label: string
			cidrAzA: string
			cidrAzB: string
			cidrAzC?: string
		}>
	}
}

export function buildStgOnboardingTagFormValues(
	tags: Account['tags'] | undefined
): Record<string, unknown> {
	if (!tags || typeof tags !== 'object') return {}
	const details = (tags as { details?: Record<string, unknown> }).details
	if (!details || typeof details !== 'object') return {}
	const out: Record<string, unknown> = {}
	for (const [key, raw] of Object.entries(details)) {
		if (raw == null) continue
		if (typeof raw === 'object' && !Array.isArray(raw) && 'value' in raw) {
			out[key] = (raw as { value?: unknown }).value
		} else {
			out[key] = raw
		}
	}
	return out
}
